import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import RecommendPeopleRail from "../components/RecommendPeopleRail";
import { apiJson } from "../utils/api";
import useSeo from "../hooks/useSeo";

function AbilityBar({ label, score }) {
  const n = Math.max(0, Math.min(100, Number(score) || 0));
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-sm">
        <span className="text-gray-700">{label}</span>
        <span className="font-semibold text-rose-600">{n}</span>
      </div>
      <div className="h-2.5 rounded-full bg-rose-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-rose-500 transition-all"
          style={{ width: `${n}%` }}
        />
      </div>
    </div>
  );
}

export default function AiGrowthPage() {
  const navigate = useNavigate();
  const userId = Number(localStorage.getItem("user_id"));
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assessing, setAssessing] = useState(false);
  const [academy, setAcademy] = useState(null);

  useSeo({
    title: "AI婚恋成长中心 | 能力评估与成长报告",
    metaDescription: "评估情绪管理、沟通、责任、关系经营与异性理解能力，生成婚恋成长报告。",
    path: "/ai/growth",
  });

  useEffect(() => {
    if (!userId) {
      navigate(`/login?redirect=${encodeURIComponent("/ai/growth")}`, { replace: true });
    }
  }, [userId, navigate]);

  const load = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [{ data }, academyRes] = await Promise.all([
        apiJson(`/api/ai/growth?user_id=${userId}`),
        apiJson(`/api/ai/growth/academy?user_id=${userId}`),
      ]);
      setItem(data?.item || null);
      setAcademy(academyRes.data || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const runAssess = async () => {
    setAssessing(true);
    try {
      const { res, data } = await apiJson("/api/ai/growth/assess", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, force: true }),
      });
      if (!res.ok) {
        const detail = data?.detail;
        throw new Error(
          typeof detail === "string" ? detail : "评估失败"
        );
      }
      setItem(data.item);
      try {
        const academyRes = await apiJson(`/api/ai/growth/academy?user_id=${userId}`);
        if (academyRes.res.ok) {
          setAcademy(academyRes.data || null);
        }
      } catch (academyErr) {
        console.error(academyErr);
      }
    } catch (err) {
      alert(err.message || "评估失败");
    } finally {
      setAssessing(false);
    }
  };

  const report = item?.report || {};
  const abilities = report.abilities || [];
  const recommended = academy?.recommended_categories || report.recommended_categories || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50 flex flex-col">
      <Header />
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-8">
        <div className="flex gap-6 items-start">
          <div className="flex-1 min-w-0 space-y-6">
          <div className="lg:hidden">
            <RecommendPeopleRail layout="strip" />
          </div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900">AI 婚恋成长中心</h1>
            <p className="text-sm text-gray-500 mt-2 max-w-xl">
              工作需要学习，婚姻同样需要学习。评估五项能力，生成《婚恋成长报告》，并推荐课程练习。
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={runAssess}
              disabled={assessing}
              className="px-4 py-2 rounded-full bg-rose-500 text-white text-sm disabled:opacity-50"
            >
              {assessing ? "评估中…" : item ? "重新评估" : "开始 AI 能力评估"}
            </button>
            <Link
              to="/ai/consultant?mode=growth"
              className="px-4 py-2 rounded-full border border-rose-300 text-rose-600 text-sm"
            >
              和成长教练聊
            </Link>
            <Link
              to="/ai/relationship-growth"
              className="px-4 py-2 rounded-full border border-violet-300 text-violet-700 text-sm"
            >
              关系成长追踪
            </Link>
            <Link
              to="/ai/behavior-profile"
              className="px-4 py-2 rounded-full border border-amber-300 text-amber-800 text-sm"
            >
              关系行为画像
            </Link>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-gray-400">加载中…</p>
        ) : !item ? (
          <div className="bg-white border border-dashed border-rose-200 rounded-2xl p-10 text-center">
            <p className="font-medium text-gray-800 mb-2">尚未生成成长报告</p>
            <p className="text-sm text-gray-500 mb-4">
              将综合你的资料、画像与测评，评估情绪、沟通、责任、关系经营与异性理解能力。
            </p>
            <button
              type="button"
              onClick={runAssess}
              className="px-5 py-2.5 rounded-full bg-rose-500 text-white text-sm"
            >
              生成《婚恋成长报告》
            </button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-5 gap-6">
            <section className="lg:col-span-2 bg-white rounded-2xl border border-rose-100 p-6 shadow-sm">
              <div className="text-center mb-6">
                <div className="text-5xl font-extrabold text-rose-600">
                  {item.ability_score}
                </div>
                <div className="text-xs text-gray-400 mt-1">当前婚恋能力综合分</div>
              </div>
              <div className="space-y-4">
                {abilities.map((a) => (
                  <AbilityBar key={a.key} label={a.label} score={a.score} />
                ))}
              </div>
            </section>

            <section className="lg:col-span-3 space-y-4">
              <div className="bg-white rounded-2xl border border-rose-100 p-6 shadow-sm">
                <h2 className="font-bold text-gray-900 mb-2">《婚恋成长报告》</h2>
                <p className="text-sm text-gray-700 leading-relaxed mb-4">
                  {report.summary || item.ai_advice}
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="bg-emerald-50 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-emerald-800 mb-2">优势</h3>
                    <ul className="text-sm text-emerald-900 space-y-1">
                      {(report.strengths || []).map((s) => (
                        <li key={s}>✓ {s}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-amber-800 mb-2">需要提升</h3>
                    <ul className="text-sm text-amber-900 space-y-1">
                      {(report.gaps || []).map((s) => (
                        <li key={s}>! {s}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                {item.ai_advice && (
                  <p className="mt-4 text-sm text-sky-900 bg-sky-50 rounded-xl p-4 whitespace-pre-line">
                    {item.ai_advice}
                  </p>
                )}
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-semibold text-gray-900">为你推荐的成长课程</h3>
                  <Link to="/courses" className="text-sm text-rose-600 underline">
                    进入 AI 婚恋学院
                  </Link>
                </div>
                {academy?.growth_direction && (
                  <p className="text-xs text-rose-700 bg-rose-50 rounded-xl px-3 py-2">
                    基于人格成长方向：{academy.growth_direction}
                  </p>
                )}
                {(academy?.recommended_courses || []).length > 0 ? (
                  <div className="space-y-3">
                    {academy.recommended_courses.map((c) => (
                      <Link
                        key={c.id}
                        to={`/courses?course=${c.id}`}
                        className="block rounded-xl border border-gray-100 hover:border-rose-300 px-4 py-3 transition"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold text-gray-900">
                              {c.title}
                            </div>
                            <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                              {c.description}
                            </div>
                            <div className="text-xs text-rose-600 mt-1.5">
                              {c.recommend_reason || c.category_label}
                              {c.duration_label ? ` · ${c.duration_label}` : ""}
                            </div>
                          </div>
                          <span className="text-xs shrink-0 px-2 py-0.5 rounded-full bg-rose-50 text-rose-600">
                            {c.category_label || c.category}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    暂无具体课程，可先进入学院浏览，或请管理员在后台添加课程。
                  </p>
                )}
                <div className="flex flex-wrap gap-2 pt-1">
                  {(academy?.tracks || []).map((t) => {
                    const hit = (t.categories || []).some((c) =>
                      recommended.includes(c)
                    );
                    return (
                      <Link
                        key={t.key}
                        to={`/courses?track=${t.key}`}
                        className={`px-3 py-1.5 rounded-full text-xs border ${
                          hit
                            ? "bg-rose-500 text-white border-rose-500"
                            : "border-gray-200 text-gray-600 hover:border-rose-300"
                        }`}
                      >
                        {t.title}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </section>
          </div>
        )}
          </div>
          <div className="hidden lg:block shrink-0">
            <RecommendPeopleRail layout="sidebar" />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
